import { useState } from 'react'
import { calculatePasswordStrength } from 'src/common/helpers'
import { FormikHelpers } from 'formik'
import { useAuth } from 'src/common/context'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { alertNotification } from 'src/ui'

interface Values {
  password: string
}

interface State {
  error?: string
  success?: string
  bar: {
    strengthColor: string
    value: number
  }
  setupMfa: boolean
  setupMfaParams: {
    secret: string
    email: string
  }
}

export const initialValues: Values = {
  password: ''
}

export const validate = (values: Values) => {
  const errors: Partial<Values> = {}
  let bar = {
    strengthColor: 'disabled',
    value: 0
  }
  if (!values.password) {
    errors.password = 'Required'
  } else {
    const { error, bar: barState } = calculatePasswordStrength(values.password)
    if (error) {
      errors.password = error
    }
    bar = barState
  }
  return { errors, bar }
}

const ResetPassword = () => {
  const { resetPassword, completeNewPassword } = useAuth()

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const email = location.state?.email

  const username = searchParams.get('user_name') || ''
  const confirmationCode = searchParams.get('confirmation_code')
  const isNewPasswordRequired = searchParams.get('newPasswordRequired') === 'true'

  // Invalid URL if: not newPasswordRequired AND missing username/confirmationCode
  const invalidURL = !isNewPasswordRequired && !(username && confirmationCode)

  const [state, setState] = useState<State>({
    error: undefined,
    success: undefined,
    bar: {
      strengthColor: 'disabled',
      value: 0
    },
    setupMfaParams: {
      email: 'undefined',
      secret: 'undefined'
    },
    setupMfa: false
  })

  const onSubmit = async (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
    const { password } = values

    try {
      if (isNewPasswordRequired) {
        // Handle NEW_PASSWORD_REQUIRED challenge
        const result = await completeNewPassword(password)
        if (result.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const sharedSecret = (result.nextStep as any).totpSetupDetails?.sharedSecret
          setState({
            ...state,
            success: 'Your password has been reset successfully',
            setupMfa: true,
            setupMfaParams: {
              email: email,
              secret: sharedSecret
            }
          })
        }
      } else if (username && confirmationCode) {
        // Handle forgot password flow
        await resetPassword(username as string, confirmationCode as string, password)
        navigate('/login')
        alertNotification('Your password has been reset successfully.', 'success')
      } else {
        throw new Error('Invalid password reset request')
      }
      setSubmitting(false)
    } catch (err) {
      setState({ ...state, error: (err as Error).message, success: undefined })
      setSubmitting(false)
    }
  }

  return { state, setState, onSubmit, invalidURL }
}

export default ResetPassword
